/**
 * JSON that stores a part many values share (by reference) once. An edit
 * history's steps share every part an edit didn't touch — a pasted table of
 * thousands of rows, say — so written plainly each step repeats it.
 *
 * `encodeShared` moves every object or array reached more than once into a
 * pool and puts a reference in its place; `decodeShared` restores them, as
 * shared objects again. A value that happens to look like a reference is
 * escaped, so any JSON value round-trips.
 */

const REF = '$shared';
const ESCAPE = '$plain';

type Json = null | boolean | number | string | Json[] | { [key: string]: Json };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

type Container = unknown[] | Record<string, unknown>;

function isContainer(value: unknown): value is Container {
  return typeof value === 'object' && value !== null;
}

/** An array or object, its members written by `encode`. */
function encodeContainer(
  item: Container,
  encode: (item: unknown) => Json,
): Json {
  if (Array.isArray(item)) return item.map(encode);
  const object: Record<string, Json> = {};
  for (const [key, entry] of Object.entries(item))
    if (entry !== undefined) object[key] = encode(entry);
  // An object that looks like a reference is wrapped, and unwrapped on read.
  return REF in object || ESCAPE in object ? { [ESCAPE]: object } : object;
}

export function encodeShared(value: unknown): {
  pool: Json[];
  value: Json;
} {
  const seen = new Map<object, number>();
  const count = (item: unknown) => {
    if (typeof item !== 'object' || item === null) return;
    const times = seen.get(item) ?? 0;
    seen.set(item, times + 1);
    if (times > 0) return;
    if (Array.isArray(item)) item.forEach(count);
    else Object.values(item).forEach(count);
  };
  count(value);
  const ids = new Map<object, number>();
  const pool: Json[] = [];
  const encode = (item: unknown): Json => {
    if (!isContainer(item))
      return item === undefined ? null : (item as Exclude<Json, object>);
    const shared = (seen.get(item) ?? 0) > 1;
    if (shared) {
      const id = ids.get(item);
      if (id !== undefined) return { [REF]: id };
    }
    const id = pool.length;
    if (shared) {
      ids.set(item, id);
      pool.push(null);
    }
    const encoded = encodeContainer(item, encode);
    if (!shared) return encoded;
    pool[id] = encoded;
    return { [REF]: id };
  };
  const encoded = encode(value);
  return { pool, value: encoded };
}

/** Reads back what {@link encodeShared} wrote; anything else is returned as is. */
export function decodeShared(stored: unknown): unknown {
  if (!isRecord(stored) || !Array.isArray(stored.pool)) return stored;
  const pool: unknown[] = stored.pool;
  const decoded = new Map<number, unknown>();
  const decode = (item: unknown): unknown => {
    if (Array.isArray(item)) return item.map(decode);
    if (!isRecord(item)) return item;
    const ref = item[REF];
    if (typeof ref === 'number' && Object.keys(item).length === 1) {
      if (decoded.has(ref)) return decoded.get(ref);
      const value = decode(pool[ref]);
      decoded.set(ref, value);
      return value;
    }
    const plain = item[ESCAPE];
    const source =
      Object.keys(item).length === 1 && isRecord(plain) ? plain : item;
    const object: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(source))
      object[key] = decode(entry);
    return object;
  };
  return decode(stored.value);
}
